import type { ModuleCode } from '../../features/modules/modules';
import apiClient from '../index';

export const getIntakeQueue = async (module: ModuleCode) => {
	const response = await apiClient.get(
		`/intake/queue?${new URLSearchParams({ module })}`,
	);
	return response.data;
};

export const getClient = async (clientId: string) => {
	const response = await apiClient.get(`/clients/${clientId}`);
	return response.data;
};

export const getFile = async (fileId: string) => {
	const response = await apiClient.get(`/files/${fileId}`);
	return response.data;
};

/** The file's sheet columns and how they are mapped (data preparation only). */
export const getFileColumns = async (fileId: string) => {
	const response = await apiClient.get(`/files/${fileId}/columns`);
	return response.data;
};

/** One of an audit's files — refused unless the file is in that audit. */
export const getAuditFile = async (auditId: string, fileId: string) => {
	const response = await apiClient.get(`/audits/${auditId}/files/${fileId}`);
	return response.data;
};

export const getFileSample = async (fileId: string) => {
	const response = await apiClient.get(`/files/${fileId}/sample?rows=20`);
	return response.data;
};

export const getFileClassificationRules = async (fileId: string) => {
	const response = await apiClient.get(
		`/files/${fileId}/classification-rules`,
	);
	return response.data;
};

export const getFirmClassificationRules = async (
	status: string,
	module?: ModuleCode,
) => {
	const params = new URLSearchParams({ status });
	if (module) params.set('module', module);
	const response = await apiClient.get(
		`/firm/classification-rules?${params}`,
	);
	return response.data;
};

export const getClassificationPreview = async (
	fileId: string,
	ruleIdsQuery: string,
) => {
	const response = await apiClient.get(
		`/files/${fileId}/classification-preview${ruleIdsQuery ? `?${ruleIdsQuery}` : ''}`,
	);
	return response.data;
};

export const getProcessingRun = async (runId: string) => {
	const response = await apiClient.get(`/processing-runs/${runId}`);
	return response.data;
};

export const getFileIngest = async (fileId: string) => {
	const response = await apiClient.get(`/files/${fileId}/ingest`);
	return response.data;
};

export const reapplyClassification = async (
	fileId: string,
	ruleIds: string[],
) => {
	const response = await apiClient.post(`/files/${fileId}/classify`, {
		ruleIds,
	});
	return response.data;
};

export const postFileVerb = async (
	fileId: string,
	verb: string,
	body?: unknown,
) => {
	const response = await apiClient.post<void>(
		`/files/${fileId}/${verb}`,
		body,
	);
	return response.data;
};

/** Soft delete (admins only). */
export const deleteFile = async (fileId: string) => {
	await apiClient.post(`/files/${fileId}/delete`);
};

export const createUploadTicket = async (input: {
	clientId: string;
	fileName: string;
	contentType: string;
	sourceSystem?: string;
	periodLabel?: string;
	module: ModuleCode;
}) => {
	const response = await apiClient.post('/files/upload-url', input);
	return response.data;
};

export const completeUpload = async (fileId: string) => {
	const response = await apiClient.post(`/files/${fileId}/upload-complete`);
	return response.data;
};

export const setFileClassificationRules = async (
	fileId: string,
	ruleIds: string[],
) => {
	const response = await apiClient.put(
		`/files/${fileId}/classification-rules`,
		{
			ruleIds,
		},
	);
	return response.data;
};

export const startFileProcessing = async (fileId: string) => {
	const response = await apiClient.post(`/files/${fileId}/process`, {});
	return response.data;
};

export const getFileEvents = async (fileId: string) => {
	const response = await apiClient.get(`/files/${fileId}/events`);
	return response.data;
};
